import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECK_INTERVAL_MS = 90 * 1000; // 90s — snappier than the HTML poll
const LS_KEY = "jumlaty_server_version_ui"; // separate from index.html's key

// Visible in-app "update available" banner. Complements the silent
// auto-reload script in `public/index.html`: instead of nuking without
// warning, we tell the customer a new version dropped and give them a
// prominent button to install it. Requested Feb 17, 2026.
// Only show the banner INSIDE the native Android app (Capacitor). On the
// web/PWA, the silent auto-reload script in `public/index.html` already
// nukes caches and force-refreshes to the latest bundle, so a visible
// banner would be redundant + annoying. Requested Feb 27, 2026.
const isNativeApp = () => {
  try {
    return !!(window?.Capacitor?.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch { return false; }
};

const UpdateAvailableBanner = () => {
  const [newVersion, setNewVersion] = useState(null);
  const [applying, setApplying] = useState(false);
  const nativeApp = isNativeApp();

  useEffect(() => {
    if (!nativeApp) return; // web version: silent auto-reload only
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/version`, { params: { t: Date.now() } });
        const server = data?.version;
        if (!server || cancelled) return;
        const seen = localStorage.getItem(LS_KEY);
        if (!seen) {
          localStorage.setItem(LS_KEY, server);
        } else if (seen !== server && newVersion !== server) {
          setNewVersion(server);
        }
      } catch { /* offline / server down — ignore */ }
    };
    check();
    const iv = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
    };
  }, [newVersion, nativeApp]);

  const applyUpdate = async () => {
    setApplying(true);
    // Store the new version so we don't re-prompt after reload.
    localStorage.setItem(LS_KEY, newVersion);
    try {
      // Nuke caches to guarantee a fresh bundle on reload
      if (window.caches?.keys) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      if (navigator.serviceWorker?.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch { /* best effort */ }
    // Force a fully fresh reload
    const u = new URL(window.location.href);
    u.searchParams.set("_v", Date.now().toString(36));
    window.location.replace(u.toString());
  };

  return (
    <AnimatePresence>
      {nativeApp && newVersion && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
          data-testid="update-available-banner"
          className="fixed top-0 inset-x-0 z-[200] p-2 pointer-events-none"
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-l from-[#0EA5E9] via-[#2563EB] to-[#7C3AED] text-white rounded-2xl shadow-2xl border-2 border-white/30 flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 pointer-events-auto relative overflow-hidden">
            {/* decorative sparkles */}
            <Sparkles className="absolute -top-1 -right-1 w-14 h-14 text-white/15 rotate-12" />
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 shrink-0">
              <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin-slow" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm sm:text-base">🎉 تحديث جديد للتطبيق متوفر!</p>
              <p className="text-[11px] sm:text-xs opacity-90">اضغط «حدّث الآن» حتى تحصل على آخر التحسينات والإصلاحات</p>
            </div>
            <button
              onClick={applyUpdate}
              disabled={applying}
              data-testid="update-apply-btn"
              className="bg-white text-[#1E3A8A] hover:bg-white/90 disabled:opacity-60 font-black rounded-full px-3 sm:px-5 py-2 text-xs sm:text-sm whitespace-nowrap shadow-lg active:scale-95 transition-transform shrink-0"
            >
              {applying ? "جاري التحديث…" : "حدّث الآن"}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdateAvailableBanner;
